/**
 * Daily Road's local results (MP-20, docs/MODES.md §4), the `dailyRoad` save section: the best run of each of the last
 * `historyDays` days (its score, result, stages, Fortress HP left and road length) and the last day whose reward was
 * paid. A day pays once (its first win); later runs that day are practice, and only raise the day's best. The paid day
 * only moves forward, so a clock set back never pays a day twice. A missed day costs nothing: there is no streak.
 */

/** One day's best run. */
export interface DailyEntry {
  readonly day: number;
  readonly score: number;
  readonly won: boolean;
  readonly stages: number;
  /** Fortress HP left. */
  readonly hp: number;
  /** Road tiles laid. */
  readonly road: number;
  /** Runs finished that day (practice included). */
  readonly runs: number;
}

/** A finished run as the state records it. */
export interface DailyResult { readonly score: number; readonly won: boolean; readonly stages: number; readonly hp: number; readonly road: number }

/** What recording a run did. */
export interface DailyRecord {
  /** False for a run already counted (a redrawn end screen). */
  readonly counted: boolean;
  /** This run is the day's paid one: its first win, and the day not paid before. */
  readonly rewarded: boolean;
  /** It beat the day's best before it (or is the day's first). */
  readonly newBest: boolean;
  /** The day's best score before this run, or null for the day's first run. */
  readonly previousBest: number | null;
  /** The day's entry after it. */
  readonly entry: DailyEntry | null;
}

export interface DailyRoadStateJson { history: DailyEntry[]; paidDay: number; lastRun: string }

const isInt = (v: unknown): v is number => Number.isInteger(v);
const nonNeg = (v: unknown): number => (isInt(v) && v >= 0 ? v : 0);

/** A better run: a higher score, then a win over a loss. */
function better(a: DailyResult, b: DailyEntry): boolean { return a.score > b.score || (a.score === b.score && a.won && !b.won); }

export class DailyRoadState {
  /** Newest day first. */
  private history: DailyEntry[] = [];
  /** The last day whose reward was paid (−1: none yet). */
  paidDay = -1;
  private lastRun = "";

  /** The kept days, newest first. */
  get entries(): readonly DailyEntry[] { return this.history; }
  entry(day: number): DailyEntry | null { return this.history.find((e) => e.day === day) ?? null; }
  /** Whether `day`'s reward is paid (or a later day's: the paid day only moves forward). */
  paid(day: number): boolean { return this.paidDay >= day; }

  /**
   * A finished run of the road of `day`, on `today`: the day's best and run count; its reward when it is a win, the day
   * not paid yet, and the road today's or yesterday's (a run started before midnight). Counted once per `key`.
   */
  record(day: number, today: number, key: string, r: DailyResult, historyDays: number): DailyRecord {
    const before = this.entry(day);
    if (key === this.lastRun) return { counted: false, rewarded: false, newBest: false, previousBest: before?.score ?? null, entry: before };
    this.lastRun = key;
    const rewarded = r.won && !this.paid(day) && day <= today && day >= today - 1;
    if (rewarded) this.paidDay = day;
    const newBest = before === null || better(r, before);
    const best = newBest ? r : before!;
    const entry: DailyEntry = { day, score: best.score, won: best.won || (before?.won ?? false), stages: best.stages, hp: best.hp, road: best.road, runs: (before?.runs ?? 0) + 1 };
    this.history = [entry, ...this.history.filter((e) => e.day !== day)].sort((a, b) => b.day - a.day);
    this.prune(today, historyDays);
    return { counted: true, rewarded, newBest, previousBest: before?.score ?? null, entry };
  }

  /** Keeps the last `historyDays` days up to `today`. */
  prune(today: number, historyDays: number): void { this.history = this.history.filter((e) => e.day > today - historyDays); }

  toJson(): DailyRoadStateJson { return { history: this.history.map((e) => ({ ...e })), paidDay: this.paidDay, lastRun: this.lastRun }; }

  static fromJson(j: unknown): DailyRoadState {
    const s = new DailyRoadState();
    if (typeof j !== "object" || j === null) return s;
    const src = j as Partial<DailyRoadStateJson>;
    if (isInt(src.paidDay) && src.paidDay >= -1) s.paidDay = src.paidDay;
    if (typeof src.lastRun === "string") s.lastRun = src.lastRun;
    for (const e of Array.isArray(src.history) ? src.history : []) {
      if (typeof e !== "object" || e === null || !isInt(e.day) || e.day < 0 || s.history.some((x) => x.day === e.day)) continue;
      s.history.push({ day: e.day, score: nonNeg(e.score), won: e.won === true, stages: nonNeg(e.stages), hp: nonNeg(e.hp), road: nonNeg(e.road), runs: Math.max(1, nonNeg(e.runs)) });
    }
    s.history.sort((a, b) => b.day - a.day);
    return s;
  }
}
